import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Pagination,
  InputAdornment,

} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as DeliveredIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon,
  Devices as DevicesIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import {
  getAllMaintenanceRecordsUnlimited,
  deleteMaintenanceRecord,
  searchMaintenanceRecords
} from '../services/maintenanceService';
import { getAllCustomers } from '../services/customerService';
import { MaintenanceRecord, MaintenanceStatus } from '../utils/types';
import { Customer } from '../types/customer';
import { statusLabels, statusIcons, statusTooltips } from '../utils/constants';
import { useCurrency } from '../contexts/CurrencyContext';

// واجهة للإحصائيات
interface Statistics {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  delivered: number;
  cancelled: number;
}

const MaintenanceRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currency } = useCurrency();

  // الحالات الأساسية
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // حالات البحث والتصفية
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<MaintenanceRecord[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // حالات القائمة والحوارات
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<string | null>(null);

  // حالة الرسائل
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // تحميل البيانات الأولية
  useEffect(() => {
    loadInitialData();
  }, []);

  // تحميل البيانات الأولية
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🚀 بدء تحميل بيانات الصيانة...');

      // تحميل متوازي للبيانات
      const [maintenanceData, customersData] = await Promise.all([
        getAllMaintenanceRecordsUnlimited(),
        getAllCustomers()
      ]);

      if (maintenanceData) {
        // ترتيب الطلبات حسب الأولوية: في الانتظار أولاً، ثم تم الإصلاح، ثم تم التسليم
        const statusPriority = {
          'pending': 1,        // قيد الانتظار أولاً
          'in_progress': 2,    // جاري العمل ثانياً
          'in-progress': 2,    // جاري العمل ثانياً (للتوافق)
          'completed': 3,      // مكتملة ثالثاً
          'delivered': 4,      // مُسلمة رابعاً
          'cancelled': 5       // مرتجعة أخيراً
        };

        const sortedRecords = maintenanceData.sort((a, b) => {
          // الترتيب الأول: حسب أولوية الحالة
          const priorityA = statusPriority[a.status as keyof typeof statusPriority] || 6;
          const priorityB = statusPriority[b.status as keyof typeof statusPriority] || 6;

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          // الترتيب الثاني: حسب التاريخ (الأحدث أولاً) في نفس الحالة
          const dateA = (a as any).createdAt?.toDate?.() || new Date((a as any).createdAt || 0);
          const dateB = (b as any).createdAt?.toDate?.() || new Date((b as any).createdAt || 0);
          return dateB.getTime() - dateA.getTime();
        });

        setRecords(sortedRecords);
        console.log(`✅ تم تحميل ${sortedRecords.length} طلب صيانة مع ترتيب حسب الأولوية`);

        // طباعة عينة من البيانات لفهم البنية
        if (sortedRecords.length > 0) {
          console.log('🔍 عينة من بيانات الطلب الأول:', {
            id: sortedRecords[0].id,
            brand: (sortedRecords[0] as any).brand,
            deviceBrand: (sortedRecords[0] as any).deviceBrand,
            size: (sortedRecords[0] as any).size,
            deviceSize: (sortedRecords[0] as any).deviceSize,
            devices: (sortedRecords[0] as any).devices,
            deviceType: (sortedRecords[0] as any).deviceType
          });
        }
      }

      if (customersData) {
        setCustomers(customersData);
        console.log(`✅ تم تحميل ${customersData.length} عميل`);
      }

    } catch (error) {
      console.error('❌ خطأ في تحميل البيانات:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل البيانات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // البحث في قاعدة البيانات
  const performSearch = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      console.log('🔍 البحث عن:', searchTerm);

      const results = await searchMaintenanceRecords(searchTerm);

      if (results && results.length > 0) {
        // ترتيب نتائج البحث حسب الأولوية أيضاً
        const statusPriority = {
          'pending': 1,        // قيد الانتظار أولاً
          'in_progress': 2,    // جاري العمل ثانياً
          'in-progress': 2,    // جاري العمل ثانياً (للتوافق)
          'completed': 3,      // مكتملة ثالثاً
          'delivered': 4,      // مُسلمة رابعاً
          'cancelled': 5       // مرتجعة أخيراً
        };

        const sortedResults = results.sort((a, b) => {
          // الترتيب الأول: حسب أولوية الحالة
          const priorityA = statusPriority[a.status as keyof typeof statusPriority] || 6;
          const priorityB = statusPriority[b.status as keyof typeof statusPriority] || 6;

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          // الترتيب الثاني: حسب التاريخ (الأحدث أولاً) في نفس الحالة
          const dateA = (a as any).createdAt?.toDate?.() || new Date((a as any).createdAt || 0);
          const dateB = (b as any).createdAt?.toDate?.() || new Date((b as any).createdAt || 0);
          return dateB.getTime() - dateA.getTime();
        });

        setSearchResults(sortedResults);
        setIsSearchMode(true);
        console.log(`✅ تم العثور على ${sortedResults.length} نتيجة مع ترتيب حسب الأولوية`);
      } else {
        setSearchResults([]);
        setIsSearchMode(true);
        console.log('❌ لم يتم العثور على نتائج');
      }

    } catch (error) {
      console.error('❌ خطأ في البحث:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء البحث',
        severity: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  }, []);

  // معالجة البحث عند الضغط على Enter
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      performSearch(searchTerm);
    }
  };

  // مسح البحث
  const clearSearch = () => {
    setSearchTerm('');
    setIsSearchMode(false);
    setSearchResults([]);
  };

  // الحصول على بيانات العميل
  const getCustomerData = useCallback((customerId: string) => {
    return customers.find(customer => customer.id === customerId);
  }, [customers]);

  // تصفية الطلبات حسب التبويب
  const filteredRecords = useMemo(() => {
    const currentRecords = isSearchMode ? searchResults : records;
    
    if (currentTab === 'all') {
      return currentRecords;
    }
    
    return currentRecords.filter(record => {
      if (currentTab === 'in_progress') {
        return record.status === 'in_progress' || (record.status as any) === 'in-progress';
      }
      return record.status === currentTab;
    });
  }, [records, searchResults, currentTab, isSearchMode]);

  // تقسيم البيانات بالصفحات
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  }, [filteredRecords, currentPage, itemsPerPage]);

  // حساب الإحصائيات
  const statistics = useMemo((): Statistics => {
    const currentRecords = isSearchMode ? searchResults : records;
    
    return currentRecords.reduce((acc, record) => {
      acc.total++;
      switch (record.status) {
        case 'pending':
          acc.pending++;
          break;
        case 'in_progress':
        case 'in-progress' as any:
          acc.inProgress++;
          break;
        case 'completed':
          acc.completed++;
          break;
        case 'delivered':
          acc.delivered++;
          break;
        case 'cancelled':
          acc.cancelled++;
          break;
      }
      return acc;
    }, {
      total: 0,
      pending: 0,
      inProgress: 0,
      completed: 0,
      delivered: 0,
      cancelled: 0
    });
  }, [records, searchResults, isSearchMode]);

  // تغيير التبويب
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
    setCurrentPage(1); // إعادة تعيين الصفحة عند تغيير التبويب
  };

  // تغيير الصفحة
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // فتح القائمة
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, recordId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRecordId(recordId);
  };

  // إغلاق القائمة
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRecordId(null);
  };

  // عرض تفاصيل الطلب باستخدام رقم الطلب
  const handleViewRecord = (recordId: string, orderNumber?: number) => {
    if (orderNumber) {
      // استخدام رقم الطلب في الرابط مع prefix خاص
      navigate(`/admin/maintenance/order-${orderNumber}`);
    } else {
      // العودة للطريقة القديمة إذا لم يكن رقم الطلب متاح
      navigate(`/admin/maintenance/${recordId}`);
    }
    handleMenuClose();
  };

  // تعديل الطلب
  const handleEditRecord = (recordId: string) => {
    navigate(`/admin/maintenance/edit/${recordId}`);
    handleMenuClose();
  };

  // حذف الطلب
  const handleDeleteRecord = (recordId: string) => {
    setRecordToDelete(recordId);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // تأكيد الحذف
  const confirmDelete = async () => {
    if (!recordToDelete) return;

    try {
      await deleteMaintenanceRecord(recordToDelete);
      setRecords(prev => prev.filter(record => record.id !== recordToDelete));
      setSnackbar({
        open: true,
        message: 'تم حذف طلب الصيانة بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('خطأ في حذف الطلب:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف الطلب',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
    }
  };

  // إغلاق حوار الحذف
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // تنسيق التاريخ
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '-';

    try {
      let date: Date;
      
      if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else {
        return '-';
      }

      if (isNaN(date.getTime())) {
        return '-';
      }

      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.error('خطأ في تنسيق التاريخ:', error);
      return '-';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          جاري تحميل بيانات الصيانة...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* العنوان الرئيسي */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 3,
          color: 'white',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Box>
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            🔧 إدارة الصيانات
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            متابعة وإدارة جميع طلبات الصيانة
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/maintenance/create')}
          sx={{ 
            borderRadius: 3,
            px: 4,
            py: 1.5,
            backgroundColor: 'white',
            color: 'primary.main',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: 'grey.100',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          طلب صيانة جديد
        </Button>
      </Box>

      {/* شريط البحث والإحصائيات */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          backgroundColor: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <Grid container spacing={3}>
          {/* البحث */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="🔍 البحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} size="small">
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'grey.50',
                  '&:hover': {
                    backgroundColor: 'grey.100'
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white'
                  }
                }
              }}
            />
          </Grid>

          {/* أزرار الإجراءات */}
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => performSearch(searchTerm)}
                disabled={isSearching || !searchTerm.trim()}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                {isSearching ? 'جاري البحث...' : 'بحث'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadInitialData}
                disabled={loading}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                تحديث
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* بطاقات الإحصائيات */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={6} sm={4} md={2}>
          <Card 
            sx={{ 
              textAlign: 'center', 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)'
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <DevicesIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {statistics.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                إجمالي الطلبات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card 
            sx={{ 
              textAlign: 'center', 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(240, 147, 251, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(240, 147, 251, 0.4)'
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <PendingIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {statistics.pending}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                قيد الانتظار
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card 
            sx={{ 
              textAlign: 'center', 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(79, 172, 254, 0.4)'
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <BuildIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {statistics.inProgress}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                جاري العمل
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card 
            sx={{ 
              textAlign: 'center', 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(67, 233, 123, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(67, 233, 123, 0.4)'
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {statistics.completed}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                مكتملة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card 
            sx={{ 
              textAlign: 'center', 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(250, 112, 154, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(250, 112, 154, 0.4)'
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <DeliveredIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {statistics.delivered}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                مُسلمة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card 
            sx={{ 
              textAlign: 'center', 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(255, 107, 107, 0.4)'
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <ErrorIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {statistics.cancelled}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                ملغية
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* تبويبات التصفية */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: 3, 
          borderRadius: 3,
          backgroundColor: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 64,
              '&.Mui-selected': {
                color: 'primary.main'
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab
            label={
              <Badge badgeContent={statistics.total} color="primary">
                الكل
              </Badge>
            }
            value="all"
          />
          <Tab
            label={
              <Badge badgeContent={statistics.pending} color="warning">
                قيد الانتظار
              </Badge>
            }
            value="pending"
          />
          <Tab
            label={
              <Badge badgeContent={statistics.inProgress} color="info">
                جاري العمل
              </Badge>
            }
            value="in_progress"
          />
          <Tab
            label={
              <Badge badgeContent={statistics.completed} color="success">
                مكتملة
              </Badge>
            }
            value="completed"
          />
          <Tab
            label={
              <Badge badgeContent={statistics.delivered} color="success">
                مُسلمة
              </Badge>
            }
            value="delivered"
          />
          <Tab
            label={
              <Badge badgeContent={statistics.cancelled} color="error">
                ملغية
              </Badge>
            }
            value="cancelled"
          />
        </Tabs>
      </Paper>

      {/* جدول البيانات */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          backgroundColor: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow 
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '& .MuiTableCell-root': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2
                  }
                }}
              >
                <TableCell>رقم الطلب</TableCell>
                <TableCell>العميل</TableCell>
                <TableCell>الهاتف</TableCell>
                <TableCell>ماركة الجهاز</TableCell>
                <TableCell>حجم الجهاز</TableCell>
                <TableCell>المشكلة</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>التكلفة</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      {isSearchMode ? 'لا توجد نتائج للبحث' : 'لا توجد طلبات صيانة'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => {
                  const customer = getCustomerData(record.customerId);
                  return (
                    <TableRow 
                      key={record.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.05)',
                          transform: 'scale(1.001)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        },
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }}
                          onClick={() => handleViewRecord(record.id, record.orderNumber)}
                        >
                          #{record.orderNumber || record.id?.slice(-6)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {customer?.name || 'غير محدد'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {customer?.phone || customer?.alternatePhone || record.customerPhone || 'غير محدد'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // البحث في جميع الحقول الممكنة لماركة الجهاز
                            const record_any = record as any;
                            return record_any.brand ||
                                   record_any.deviceBrand ||
                                   (record_any.devices && record_any.devices[0]?.brand) ||
                                   'غير محدد';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // البحث في جميع الحقول الممكنة لحجم الجهاز
                            const record_any = record as any;
                            return record_any.size ||
                                   record_any.deviceSize ||
                                   (record_any.devices && record_any.devices[0]?.size) ||
                                   'غير محدد';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {record.issue || 'غير محدد'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: (() => {
                              const status = record.status as MaintenanceStatus;
                              switch (status) {
                                case 'pending': return 'warning.light';
                                case 'in_progress':
                                case 'in-progress' as any: return 'info.light';
                                case 'completed': return 'success.light';
                                case 'delivered': return 'primary.light';
                                case 'cancelled': return 'error.light';
                                default: return 'grey.300';
                              }
                            })(),
                            color: (() => {
                              const status = record.status as MaintenanceStatus;
                              switch (status) {
                                case 'pending': return 'warning.dark';
                                case 'in_progress':
                                case 'in-progress' as any: return 'info.dark';
                                case 'completed': return 'success.dark';
                                case 'delivered': return 'primary.dark';
                                case 'cancelled': return 'error.dark';
                                default: return 'grey.600';
                              }
                            })(),
                          }}
                          title={statusTooltips[record.status as MaintenanceStatus] || statusLabels[record.status as MaintenanceStatus] || record.status}
                        >
                          {statusIcons[record.status as MaintenanceStatus] || '●'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate((record as any).createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {(record as any).totalCost ? `${(record as any).totalCost} جنيه` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, record.id)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* التنقل بين الصفحات */}
        {filteredRecords.length > itemsPerPage && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={Math.ceil(filteredRecords.length / itemsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Paper>

      {/* قائمة الإجراءات */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          if (selectedRecordId) {
            const selectedRecord = filteredRecords.find(r => r.id === selectedRecordId);
            handleViewRecord(selectedRecordId, selectedRecord?.orderNumber);
          }
        }}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          عرض التفاصيل
        </MenuItem>
        <MenuItem onClick={() => selectedRecordId && handleEditRecord(selectedRecordId)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          تعديل
        </MenuItem>
        <MenuItem onClick={() => selectedRecordId && handleDeleteRecord(selectedRecordId)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" />
          حذف
        </MenuItem>
      </Menu>

      {/* حوار تأكيد الحذف */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف طلب الصيانة هذا؟ لا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            إلغاء
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسائل التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaintenanceRecordsPage;
